import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { EntityConfigService, EntityService, Field, StoredEntityService } from '@dot-it/jml-lib'
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'

enum FilterType {
  VISIBLEFIELDS = 'visibleFields',
  TEMPLATE = 'template'
}

@Component({
  selector: 'jml-lib-fields-filter',
  templateUrl: './fields-filter.component.html',
  styleUrls: ['./fields-filter.component.scss']
})
export class FieldsFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() refreshTrigger: Subject<[string, number]>
  @Input() entityId: number
  @Input() dtoType: string
  @Input() filterType: FilterType = FilterType.TEMPLATE

  /** fields to display */
  fields$: Observable<Field[]>
  fields: Field[] // extract from fields$

  // Todo: let all the other inline edit components work with the Observable
  entityData: object
  entityData$: Observable<object>

  // TODO: this is a hack to emulate the proper store bahaviour (we would expect a .next() from within the store when an update is triggered
  $entityData: BehaviorSubject<object>
  subscription: Subscription

  // cache of already loaded templates
  templateMap: Map<number, Field[]> = new Map()

  constructor(
    private entityService: EntityService,
    private entityConfigService: EntityConfigService,
    private storedEntityService: StoredEntityService
  ) {}

  // Only use FilterType.TEMPLATE! For visibleFields filter use EntityHeader.
  // WHY? Because date edit as well as permissions are not considered for now.
  // TODO: Implement components FieldsFilter and FieldsView in a way that it substitutes EntityHeader and FieldGroup

  ngOnInit() {
    this.init()
  }

  init() {
    if (!this.$entityData) {
      this.$entityData = new BehaviorSubject<object>(undefined)
      this.entityData$ = this.$entityData.asObservable()

      this.fields$ = this.entityData$.pipe(
        filter((entityData) => !!entityData),
        switchMap((entityData) => this.getFilteredFields(entityData['_templateId_'])),
        tap((fields) => (this.fields = fields))
      )
    }

    // TODO: as long as we need the artificial subject, we need to subscribe to the getEntity$ call, as this Observable wont be subscribed by the html async pipe
    if (!!this.subscription) {
      this.subscription.unsubscribe()
    }
    this.subscription = this.storedEntityService
      .getEntity$(this.dtoType, this.entityId, { enforceFreshBackendRequest: false, partialEntityIsSufficient: false })
      .pipe(
        map((entity) => entity.fields),
        tap((entityData) => {
          this.entityData = entityData
          this.$entityData.next(entityData)
        })
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.entityId?.isFirstChange()) {
      this.init()
    }
  }

  private getFilteredFields(templateId: number | undefined): Observable<Field[]> {
    // fields to be viewed depend on filterType
    let fields$: Observable<Field[]>
    switch (this.filterType) {
      // filter for visible fields
      case FilterType.VISIBLEFIELDS: {
        fields$ = this.entityConfigService.getVisibleFields$(this.dtoType, true)
        break
      }

      // Fields that are coming from a template (those are actually not part of the entity configuration)
      // The temnplateId is part of the entity though. Backend call required to get the fields for the given templateId.
      case FilterType.TEMPLATE: {
        fields$ = !templateId ? of([]) : this.getTemplateFields$(templateId)
        break
      }

      // If no filterType matches there is an error in the DtoConfig.
      default: {
        console.error(
          `DtoConfig has errors! \nDtoType: ${this.dtoType}\n EntityAttachment: FieldSubset\n Error: "filterType" not valid or not specified!`
        )
        fields$ = of([])
        break
      }
    }
    return fields$
  }

  updateField(field: string, value: any) {
    this._update({ [field]: value ?? null })
  }

  completeCellEdit(event: any) {
    this._update({ [event.field]: event?.data?.[event.field] })
  }

  private _update(data: any) {
    const lastUpdate = this.entityData['_lastUpdate_']

    this.storedEntityService
      .updateEntity$(this.fields, this.dtoType, this.entityId, data, lastUpdate)
      .pipe(
        tap((entity) => {
          this.$entityData.next(entity.fields)
          this.refreshTrigger?.next([this.dtoType, this.entityId])
          this.init()
        })
      )
      .subscribe()
  }

  private getTemplateFields$(templateId: number): Observable<Field[]> {
    return this.templateMap.has(templateId)
      ? of(this.templateMap.get(templateId))
      : this.entityService
          .getTemplateFields$(templateId, this.dtoType)
          .pipe(tap((fields) => this.templateMap.set(templateId, fields)))
  }

  ngOnDestroy(): void {
    if (!!this.subscription) {
      this.subscription.unsubscribe()
    }
  }
}
